import React, { useState, useEffect } from 'react';
import {  BarChart, Bar, XAxis, YAxis, ZAxis, CartesianGrid,
  ScatterChart, Scatter, Line, LineChart, Tooltip, Legend, Dot,
  Cell, PieChart, Pie, Label, Text, LabelList } from 'recharts';
import { toMoney, toPerc, toFixed } from './Functions';
import { colors_sura_inv } from '../shared/data';
import moment from 'moment';
import 'moment/locale/es';

const CustomizedShape = (props) => {
  const {cx, cy, fill, name} = props;
  return (
    <g>
      <Dot cx={cx} cy={cy} r={7} fill={fill} stroke={'white'} strokeWidth={1}/>
      <g transform={`translate(${cx},${cy})`}>
        <text x={10} y={0} dy={4} textAnchor="left" fontSize="0.90em" fill="#707372">{name}</text>
      </g>
    </g>
   );
};

const PercTooltip = ({ active, payload, label, fontSize='11px' }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p style={{ margin: 0, fontSize }}>{`${label} : ${payload[0].value.toFixed(2)}%`}</p>
      </div>
    );
  }
  return null;
};
const PiePercTooltip = ({ active, payload }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p className="label">{`${payload[0].name} : ${payload[0].value.toFixed(2)}%`}</p>
      </div>
    );
  }
  return null;
};

const ValTooltip = ({ active, payload, label, fontSize='11px' }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p style={{ margin: 0, fontSize }}>{`${label} : ${payload[0].value.toFixed(2)}`}</p>
      </div>
    );
  }
  return null;
};
const MoneyTooltip = ({ active, payload, label, fontSize='11px' }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p style={{ margin: 0, fontSize }}>{`${label} : ${toMoney(payload[0].value)}`}</p>
      </div>
    );
  }
  return null;
};

const MultValueTooltip = ({ active, payload, label, fontSize='11px' }) => {
  if (active) {
    const bars = payload.map((item,i)=>{
      return(
        <p style={{ margin: 0, fontSize }} key={i}>{`${item.dataKey}: ${item.value.toFixed(2)}`}</p>
      )
    });
    return (
      <div className="custom-tooltip">
        <b>{label}</b>
        {bars}
      </div>
    );
  }
  return null;
};

const MultMoneyTooltip = ({ active, payload, label }) => {
  if (active) {
    const bars = payload.map((item,i)=>{
      return(
        <p key={i}>{`${item.dataKey}: ${toMoney(item.value)}`}</p>
      )
    });
    return (
      <div className="custom-tooltip">
        <b>{label}</b>
        {bars}
      </div>
    );
  }
  return null;
};

const MultFixedTooltip = ({ active, payload, label }) => {
  if (active) {
    const bars = payload.map((item,i)=>{
      return(
        <p className="m-1" key={i}>{`${item.dataKey}: ${toFixed(item.value)}`}</p>
      )
    });
    return (
      <div className="custom-tooltip">
        <b>{label}</b>
        {bars}
      </div>
    );
  }
  return null;
};

const MultPercTooltip = ({ active, payload, label, fontSize = '10px' }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="custom-tooltip"
      style={{
        fontSize,
        padding: '4px 6px',   // reduce padding
        border: '1px solid #ccc',
        borderRadius: '4px',
        backgroundColor: 'white',
        lineHeight: 1.2,      // tighter vertical spacing
        maxWidth: '150px'     // optional: prevent it from stretching
      }}
    >
      <b style={{ fontSize }}>{label}</b>
      {payload.map((item, i) => (
        <p
          key={i}
          style={{
            margin: 3,
            fontSize,
            color: item.color
          }}
        >
          {`${item.dataKey}: ${toPerc(item.value, 3)}`}
        </p>
      ))}
    </div>
  );
};

const MultDatePercTooltip = ({ active, payload, label, fontSize='10px' }) => {
  if (active) {
    const bars = payload.map((item,i)=>{
      return(
        <p key={i} style={{ fontSize }}>{`${item.dataKey}: ${toPerc(item.value, 3)}`}</p>
      )
    });
    return (
      <div className="custom-tooltip">
        <b style={{ fontSize }}>{moment(label).format("ll").slice(0, 18)}</b>
        {bars}
      </div>
    );
  }
  return null;
};

const MultDateMoneyTooltip = ({ active, payload, label, fontSize='10px' }) => {
  if (active) {
    const bars = payload.map((item,i)=>{
      return(
        <p key={i} style={{ fontSize }}>{`${item.dataKey}: ${toMoney(item.value)}`}</p>
      )
    });
    return (
      <div className="custom-tooltip">
        <b style={{ fontSize }}>{moment(label).format("ll").slice(0, 18)}</b>
        {bars}
      </div>
    );
  }
  return null;
};

const dateTick = ({ x, y, payload }) => {
 const dateTip = moment(payload.value).locale('es').format("L");
    return (
   <g transform={`translate(${x},${y})`}>
  <text className='xlabel' x={23} y={0} dy={14} fontSize="0.90em" fontFamily="bold" textAnchor="end" fill="#707372">
    {dateTip}</text>
   </g>
  );
 };

const CustomTick = (props) => {
  const { x, y, payload } = props;
  return (
    <text x={x} y={y} dy={16} textAnchor="middle" fill="#666" fontSize={11}>
      {payload.value}
    </text>
  );
};

const CustomTickY = (props) => {
  const { x, y, payload } = props;
  return (
    <text 
      x={x} 
      y={y} 
      dy={3} 
      textAnchor="end" 
      fill="#666" 
      fontSize={props.fontSize}
      fontFamily={props.fontFamily || "Nunito, sans-serif"}
      >
      {payload.value}
    </text>
  );
};


// helper to measure text width
const measureTextWidth = (text, font = "12px Arial") => {
  if (typeof document === "undefined") return 0;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = font;
  return ctx.measureText(text).width;
};

export const MultBarPlot = (props) => {
  const bars = Object.keys(props.data[0])
    .filter((x) => x !== (props.nameKey || "name"))
    .map((item, i) => (
      <Bar
        key={i}
        dataKey={item}
        fill={props.colors[i]}
        barSize={props.barsize || 5}
      />
    ));

  // compute dynamic left margin if vertical
  let dynamicLeftMargin = props.ml || 5;
  if (props.vertical && !props.ml) {
    const labels = props.data.map((d) => String(d[props.nameKey || "name"]));
    const fontSize = props.fontsize ? `${props.fontsize}px Arial` : "12px Arial";
    const widths = labels.map((lbl) => measureTextWidth(lbl, fontSize));
    const maxWidth = Math.max(...widths);
    const measured = maxWidth + 10;
    dynamicLeftMargin = measured; //Math.min(Math.max(measured, 40), 50);
  }
  const margin = {
    top: props.top || 5,
    right: 20,
    left: props.left || 5,
    bottom: 5,
  };

  if (props.vertical) {
    return (
      <BarChart
        key={dynamicLeftMargin}
        width={props.width || 500}
        height={props.height || 400}
        data={props.data}
        margin={margin}
        layout="vertical"
      >
        <CartesianGrid strokeDasharray="3 3" />
        {props.ymoneyAxis ? (
          <YAxis
            type={props.ytype}
            width={dynamicLeftMargin}
            dataKey={props.nameKey || "name"}
            tickFormatter={(x) => toMoney(x)}
          />
        ) : props.ypercAxis ? (
          <YAxis
            type={props.ytype}
            width={dynamicLeftMargin}
            dataKey={props.nameKey || "name"}
            tickFormatter={(x) => toPerc(x)}
          />
        ) : (
          <YAxis
            tick={<CustomTickY fontSize={String(props.fontsize)} />}
            type={props.ytype}
            width={dynamicLeftMargin}
            interval={props.interval}
            dataKey={props.nameKey || "name"}
          />
        )}
        {props.xmoneyAxis ? (
          <XAxis type={props.xtype} tickFormatter={(x) => toMoney(x)} />
        ) : props.xpercAxis ? (
          <XAxis type={props.xtype} tickFormatter={(x) => toPerc(x)} />
        ) : (
          <XAxis type={props.xtype} />
        )}
        {props.moneyTooltip ? (
          <Tooltip content={<MultMoneyTooltip />} />
        ) : props.percTooltip ? (
          <Tooltip content={<MultPercTooltip />} />
        ) : (
          <Tooltip content={<MultValueTooltip />} />
        )}
        {props.legend ? (
          <Legend
            align="left"
            wrapperStyle={{
              marginLeft: dynamicLeftMargin + (props.width || 500) * 0.05,
            }}
          />
        ) : null}
        {bars}
      </BarChart>
    );
  } else {
    return (
      <BarChart
        key={dynamicLeftMargin}
        width={props.width || 500}
        height={props.height || 400}
        data={props.data}
        margin={margin}
        layout="horizontal"
      >
        <CartesianGrid strokeDasharray="3 3" />
        {props.ymoneyAxis ? (
          <YAxis
            type={props.ytype}
            width={props.ml}
            tickFormatter={(x) => toMoney(x)}
          />
        ) : props.ypercAxis ? (
          <YAxis type={props.ytype} tickFormatter={(x) => toPerc(x)} />
        ) : (
          <YAxis type={props.ytype} />
        )}
        {props.xmoneyAxis ? (
          <XAxis
            type={props.xtype}
            dataKey={props.nameKey || "name"}
            tickFormatter={(x) => toMoney(x)}
          />
        ) : props.xpercAxis ? (
          <XAxis
            type={props.xtype}
            dataKey="name"
            tickFormatter={(x) => toPerc(x)}
          />
        ) : (
          <XAxis
            type={props.xtype}
            tick={<CustomTick />}
            dataKey={props.nameKey || "name"}
          />
        )}
        {props.moneyTooltip ? (
          <Tooltip content={<MultMoneyTooltip />} />
        ) : props.percTooltip ? (
          <Tooltip content={<MultPercTooltip />} />
        ) : (
          <Tooltip content={<MultValueTooltip />} />
        )}
        <Legend
          align="left"
          wrapperStyle={{
            marginLeft: dynamicLeftMargin + (props.width || 500) * 0.05,
          }}
        />
        {bars}
      </BarChart>
    );
  }
};

export const BarPlot = (props) => {
  const data = props.x.map((val, i) => {
    return (
      { x: val, y: props.y[i] }
    );
  });
  const margin = { top: props.top || 5, right: 20, left: 5, bottom: 20 };
  // compute dynamic left margin if vertical
  let dynamicLeftMargin = props.ml || 5;
  if (props.layout==="vertical" && !props.ml) {
    const labels = data.map((d) => {
      if (props.ytype=="number"){
       if (props.ymoneyAxis){
         return toMoney(d.y);
       } else if (props.ypercAxis){
         return toPerc(d.y);
       } else {
         return String(d.y);
       }
      }else{
        return String(d.y);  
      }
    });
    const fontSize = props.fontsize ? `${props.fontsize}px Arial` : "12px Arial";
    const widths = labels.map((lbl) => measureTextWidth(lbl, fontSize));
    const maxWidth = Math.max(...widths);
    const measured = maxWidth + 10;
    dynamicLeftMargin = measured; //Math.min(Math.max(measured, 40), 50);
  }

  if (props.layout === "vertical") {
    const barColor = (entry) => entry.x >= 0 ? (props.color || "#2803fc") : (props.colorNeg || props.color || '#000000');
    return (
      <BarChart
        width={props.width || 500}
        height={props.height || 400}
        data={data}
        margin={margin}
        layout={props.layout}
      >
        <CartesianGrid strokeDasharray="3 3" />
        {props.ymoneyAxis
          ? <YAxis dataKey='y' type={props.ytype} name={props.ylabel} tickFormatter={(x) => toMoney(x)} />
          : props.ypercAxis
            ? <YAxis dataKey='y' type={props.ytype} name={props.ylabel} tickFormatter={(x) => toPerc(x)} />
            : <YAxis dataKey='y' interval={props.interval} tick={<CustomTickY fontSize={String(props.fontsize)} />} width={dynamicLeftMargin} type={props.ytype} name={props.ylabel} />
        }
        {props.xmoneyAxis
          ? <XAxis dataKey='x' type={props.xtype} name={props.xlabel} angle={props.angle} tickFormatter={(x) => toMoney(x)} />
          : props.xpercAxis
            ? <XAxis dataKey='x' type={props.xtype} name={props.xlabel} angle={props.angle} tickFormatter={(x) => toPerc(x)} domain={['dataMin - 1', 'dataMax + 1']} />
            : <XAxis dataKey='x' name={props.xlabel} angle={props.angle} />
        }
        {props.percTooltip
          ? <Tooltip content={<PercTooltip />} />
          : props.moneyTooltip
            ? <Tooltip content={<MoneyTooltip />} />
            : <Tooltip content={<ValTooltip />} />
        }
        <Bar
          dataKey='x'
          name={props.ylabel}
          barSize={props.barsize}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={barColor(entry)} />
          ))}
        </Bar>
      </BarChart>
    );
  } else {
    const barColor = (entry) => entry.y >= 0 ? (props.color || "#2803fc") : (props.colorNeg || props.color || '#000000');
    return (
      <BarChart
        width={props.width || 500}
        height={props.height || 400}
        data={data}
        margin={margin}
        layout={props.layout}
      >
        <CartesianGrid strokeDasharray="3 3" />
        {props.ymoneyAxis
          ? <YAxis dataKey='y' type={props.ytype} name={props.ylabel} width={50} tickFormatter={(x) => toMoney(x)} />
          : props.ypercAxis
            ? <YAxis dataKey='y' type={props.ytype} name={props.ylabel} width={props.ml} tickFormatter={(x) => toPerc(x)} />
            : <YAxis dataKey='y' type={props.ytype} name={props.ylabel} />
        }
        {props.xmoneyAxis
          ? <XAxis dataKey='x' type={props.xtype} name={props.xlabel} angle={props.angle} tickFormatter={(x) => toMoney(x)} />
          : <XAxis dataKey='x' type={props.xtype} name={props.xlabel} tick={<CustomTick />} interval={props.interval} width={30} angle={props.angle} />
        }
        {props.percTooltip
          ? <Tooltip content={<PercTooltip />} />
          : props.moneyTooltip
            ? <Tooltip content={<MoneyTooltip />} />
            : <Tooltip content={<ValTooltip />} />
        }
        <Bar
          dataKey='y'
          name={props.ylabel}
          barSize={props.barsize}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={barColor(entry)} />
          ))}
        </Bar>
      </BarChart>
    );
  }
};

const LinePercTooltip = ({ active, payload }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p>{`${payload[0].name}: ${payload[0]['value'].toFixed(2)}`}</p>
        <p>{`${payload[1].name}: ${payload[1]['value'].toFixed(2)}%`}</p>
      </div>
    );
  }
  return null;
};

const LineTooltip = ({ active, payload }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <p>{`${payload[0].name}: ${payload[0]['value'].toFixed(2)}`}</p>
        <p>{`${payload[1].name}: ${payload[1]['value'].toFixed(2)}`}</p>
      </div>
    );
  }
  return null;
};

export const LinePlot = (props) => {
    const data = props.x.map((val,i) => {
      return(
        {x:val, y:props.y[i]}
      );
    });
    return (
      <ScatterChart width={500} height={400}
        margin={{
          top: 20, right: 90, bottom: 20, left: 20,
        }}
      >
          <XAxis type="number" dataKey="x" name={props.xlabel} domain={['auto', 'auto']}>
            <Label value={props.xlabel} position="insideBottom" offset={0} />
          </XAxis>
          <YAxis type="number"
            label={{ value: props.ylabel, angle: -90, position: 'insideLeft' }}
            dataKey="y" name={props.ylabel} domain={['auto', 'auto']}/>
          <CartesianGrid stroke="#eee" strokeDasharray="5 5"/>
          <Tooltip cursor={{ strokeDasharray: '3 3' }} content={props.percTooltip?<LinePercTooltip/>:<LineTooltip />} allowEscapeViewBox={{ x: true, y: true }}></Tooltip>
          <Scatter name="Frontera" data={data} fill={props.color} line shape="cross" />
          {props.points!==null ? <Scatter name="scatter" data={props.points} fill={props.color} shape={<CustomizedShape />} />:null}
        </ScatterChart>
      );
  };

  export const MultLinePlot = (props) => {
      const lines = props.ykeys.map((val,i) => {
        return(
          <Line key={i} type="monotone" dataKey={val} name={val} stroke={props.colors[i]} dot={false}/>
        );
      });
      return (
        <LineChart
          width={props.width || 500} height={props.height || 400}
          data={props.data}
          margin={{
            top: 20, right: 20, bottom: 20, left: 20,
          }}
        >
            {props.datetick ? <XAxis type={props.xtype} dataKey={props.xkey} tick={dateTick} name={props.xlabel} /> : <XAxis type={props.xtype} dataKey={props.xkey} name={props.xlabel} />}
            <YAxis type={props.ytype} tickFormatter={props.ypercAxis ? (x)=>toPerc(x) : null} name={props.ylabel} domain={['auto', 'auto']}/>
            <CartesianGrid strokeDasharray="5 5"/>
            {props.percTooltip ? props.datetick ? <Tooltip content={<MultDatePercTooltip />} /> : <Tooltip content={<MultPercTooltip />} /> : <Tooltip cursor={{ strokeDasharray: '3 3' }} />}
            <Legend iconType='square'/>
            {lines}
          </LineChart>
        );
    };


  const ScatterPercTooltip = ({ active, payload, fontSize='11px', xlabel, ylabel }) => {
    if (active) {
      return (
        <div className="custom-tooltip" style={{ fontSize }}>
          <p className="label" style={{ fontSize }}>{`${payload[2].value}`}</p>
          <p style={{ margin: 0, fontSize }}>{`${xlabel}: ${payload[0].value.toFixed(2)}%`}</p>
          <p style={{ margin: 0, fontSize }}>{`${ylabel}: ${payload[1].value.toFixed(2)}%`}</p>
        </div>
      );
    }
    return null;
  };
  const ScatterTooltip = ({ active, payload, fontSize='11px', xlabel, ylabel }) => {
    if (active) {
      return (
        <div className="custom-tooltip" style={{ fontSize }}>
          <p className="label" style={{ fontSize }}>{`${payload[2].value}`}</p>
          <p style={{ margin: 0, fontSize }}>{`${xlabel}: ${payload[0].value.toFixed(2)}`}</p>
          <p style={{ margin: 0, fontSize }}>{`${ylabel}: ${payload[1].value.toFixed(2)}`}</p>
        </div>
      );
    }
    return null;
  };


export const ScatterPlot = (props) => {
  const {
    width = 500,
    height = 400,
    top = 10,
    mr = 20,
    ml,
    xlabel,
    ylabel,
    xAxisId,
    yAxisId,
    zAxisId,
    data,
    data2,
    dataName,
    dataName2,
    color,
    color2,
    xpercAxis,
    ypercAxis,
    percTooltip,
    legend,
    iconType = 'circle',
    showLabels,
    labelKey,
    fontsize
  } = props;

  let dynamicLeftMargin = ml || 5;
  const labels = data.map((d) => String(d[yAxisId || "name"]));
  const fontSize = fontsize ? `${fontsize}px Arial` : "12px Arial";
  const widths = labels.map((lbl) => measureTextWidth(lbl, fontSize));
  const maxWidth = Math.max(...widths);
  const measured = maxWidth;
  dynamicLeftMargin = measured; //Math.min(Math.max(measured, 40), 50);

  return (
    <ScatterChart
      width={width}
      height={height}
      margin={{
        top,
        right: mr,
        bottom: 30,  // more space for x-axis label
        left: dynamicLeftMargin,    // more space for y-axis label
      }}
    >
      <XAxis
        type="number"
        dataKey={xAxisId}
        name={xlabel}
        domain={['auto', 'auto']}
        tickFormatter={(value) =>
          xpercAxis ? toPerc(value) : toFixed(value)
        }
      >
        <Label value={xlabel} position="bottom" offset={10} />
      </XAxis>
      <YAxis
        type="number"
        dataKey={yAxisId}
        name={ylabel}
        domain={['auto', 'auto']}
        tickFormatter={(value) =>
          ypercAxis ? toPerc(value) : toFixed(value)
        }
        label={{
          value: ylabel,
          angle: -90,
          position: 'insideLeft',
          offset: 0,
          dx: -15, // move left or right
          dy: 0,   // vertical fine-tune if needed
          style: { textAnchor: 'middle' }, // centers text
        }}
      />

      <ZAxis type="category" dataKey={zAxisId} name="asset" />

      <CartesianGrid stroke="#eee" strokeDasharray="5 5" />

      <Tooltip
        cursor={{ strokeDasharray: '3 3' }}
        content={
          percTooltip ? (
            <ScatterPercTooltip xlabel={xlabel} ylabel={ylabel} />
          ) : (
            <ScatterTooltip xlabel={xlabel} ylabel={ylabel} />
          )
        }
      />

      {legend && (
        <Legend
          iconType={iconType}
          align="left"
          verticalAlign="top"
          wrapperStyle={{
            marginLeft: ml + width * 0.2,
            marginTop: height - 12,
          }}
        />
      )}

      <Scatter shape={iconType} name={dataName} data={data} fill={color}>
        {showLabels && (
          <LabelList
            dataKey={labelKey || dataName}
            content={({ x, y, value }) => (
              <text
                x={x + 5}
                y={y}
                textAnchor="start"
                fontSize="10"
                fill="#333"
              >
                {value}
              </text>
            )}
          />
        )}
      </Scatter>

      {data2 && (
        <Scatter shape={iconType} name={dataName2} data={data2} fill={color2}>
          {showLabels && (
            <LabelList
              dataKey={labelKey || dataName2}
              position="top"
              style={{ fontSize: 10 }}
            />
          )}
        </Scatter>
      )}
    </ScatterChart>
  );
};

  export const StackedBarPlot = (props) => {
    // const colors = ['#C7C9C7', '#9EA2A2','#D1CCDB', '#B7A99A'];
    const colors = [
        '#000000',"#1215E3", "#FAEA2F", 
        '#9EA2A2','#C7C9C7', '#768692',
        '#0E0E95','#333333', '#E9E9F0',
        "#0A0A6B",
        '#7A6855',
        '#6A0DAD', 
        '#8c8888', '#98A4AE',
        '#494949',"#383AC7", "#9F9204", 
        '#505353','#949994', '#4D5E6B'
    ];
    const var_names = Object.keys(props.data[0]).filter(x => x!=='name');
    const bars = var_names.map((val,i)=>{
      return(<Bar 
        key={val} 
        dataKey={val} 
        stackId="a" 
        fill={colors[i]} 
        barSize={props.barsize}
        />);
    });
    return (
      <BarChart
       width={props.width || 500}
       height={props.height ||  400}
       data={props.data}
       margin={{
         top: 5, right: 5, bottom: 5, left: 0,
       }}
     >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      {props.ymoneyAxis
        ? <YAxis type={props.ytype} name={props.ylabel} width={50} tickFormatter={(x) => toMoney(x)} />
        : props.ypercAxis
          ? <YAxis type={props.ytype} name={props.ylabel} domain={[0,100]} width={props.ml} tickFormatter={(x) => toPerc(x)} />
          : <YAxis type={props.ytype} name={props.ylabel} />
      }
      {props.percTooltip
        ? <Tooltip content={<MultPercTooltip />} />
        : props.moneyTooltip
          ? <Tooltip content={<MultMoneyTooltip />} />
          : <Tooltip content={<MultValueTooltip />}/>
      }
      {props.legend ?
      <Legend
        layout="vertical"
        align="center"
        wrapperStyle={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        height={props.legendHeight || 100}
      /> : null}

        {bars}
     </BarChart>

    );
  }

  export const PiePlot = (props) => {
    const colors = colors_sura_inv;

    const sorted_data = [...props.data].sort(
      (a, b) => (a[props.dataKey || "value"] < b[props.dataKey || "value"] ? 1 : -1)
    );
    const cells =  sorted_data.map((entry, i) => <Cell key={i} fill={colors[i]} name={entry[props.nameKey || "name"]}/>);
      return(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PieChart 
            width={props.width || 500}
            height={props.height || 450}
  
            margin={{top: 5, right: 5, bottom: 5, left: 5}}
            >
            <Pie
              data={sorted_data} 
              dataKey={props.dataKey || "value"} 
              nameKey={props.nameKey || "name"}
              cx={props.width/2} 
              cy={(props.height-100)/2} 
              label={true} 
              labelLine={true} 
              outerRadius={props.outerRadius || props.height/4}
              fill="#8884d8"
              label={props.percTooltip ? (data)=>data.payload[props.dataKey || "value"].toFixed(2)+'%' : (data)=>data.payload[props.dataKey || "value"].toFixed(2)} 
            >
            {cells}
            </Pie>
          {props.legend ? 
            <Legend 
              align="center"
              wrapperStyle={{ display: 'flex', justifyContent: 'center', width: '100%' }}
              height={props.legendHeight || 120}
            /> : null}
          {props.percTooltip ? <Tooltip content={<PiePercTooltip />}/> : <Tooltip/>}
          </PieChart>
        </div>
      );
   };


export const MultLineTsPlot = (props) => {
  const hasFixedWidth = props.width != null; // check if width was passed

  const [chartWidth, setChartWidth] = useState(() => {
    return hasFixedWidth ? props.width : getResponsiveWidth();
  });

  function getResponsiveWidth() {
    const w = window.innerWidth;
    if (w < 768) return Math.min(w-200, 400);    // sm
    if (w < 1200) return 500;   // md
    return 800;                // lg
  }

  useEffect(() => {
    if (hasFixedWidth) return; // don't attach resize listener if fixed width
    const handleResize = () => setChartWidth(getResponsiveWidth());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasFixedWidth]);

  const width = hasFixedWidth ? props.width : chartWidth;

  const lines = props.ykeys.map((val, i) => (
    <Line
      key={i}
      type="monotone"
      dataKey={val}
      name={val}
      stroke={props.colors[i]}
      dot={props.dot || false}
    />
  ));

  return (
    <LineChart
      width={width}
      height={props.height || 400}
      data={props.data}
      margin={{
        top: props.top || 5,
        right: props.right || 20,
        bottom: props.bottom || 5,
        left: 5,
      }}
    >
      {props.datetick == null || props.datetick ? (
        <XAxis type={props.xtype} dataKey={props.xkey} tick={dateTick} name={props.xlabel} />
      ) : (
        <XAxis type={props.xtype} dataKey={props.xkey} name={props.xlabel} />
      )}

      {props.ymoneyAxis ? (
        <YAxis
          width={props.ml}
          type={props.ytype}
          name={props.ylabel}
          tickFormatter={(x) => toMoney(x)}
          domain={["auto", "auto"]}
        />
      ) : props.ypercAxis ? (
        <YAxis
          width={props.ml}
          type={props.ytype}
          name={props.ylabel}
          tickFormatter={(x) => toPerc(x, 0)}
          domain={["auto", "auto"]}
        />
      ) : (
        <YAxis
          width={props.ml}
          type={props.ytype}
          name={props.ylabel}
          domain={["auto", "auto"]}
        />
      )}

      {props.percTooltip ? (
        props.datetick ? (
          <Tooltip contentStyle={{ fontSize: "10px" }} content={<MultDatePercTooltip />} />
        ) : (
          <Tooltip contentStyle={{ fontSize: "10px" }} content={<MultPercTooltip />} />
        )
      ) : props.moneyTooltip ? (
        props.datetick ? (
          <Tooltip contentStyle={{ fontSize: "10px" }} content={<MultDateMoneyTooltip />} />
        ) : (
          <Tooltip contentStyle={{ fontSize: "10px" }} content={<MultMoneyTooltip />} />
        )
      ) : (
        <Tooltip content={<MultFixedTooltip />} cursor={{ strokeDasharray: "3 3" }} />
      )}

      {props.legend ? (
        <Legend
          iconType="square"
          align="left"
          wrapperStyle={{
            marginTop: 10,
            marginLeft: (props.ml || 100) + width * 0.05,
          }}
        />
      ) : null}

      {lines}
    </LineChart>
  );
};